<template>
  <div
    ref="root"
    class="ui-info"
    @keydown.esc="docId ? null : (open = false)"
  >
    <button
      class="help-trigger"
      type="button"
      :aria-expanded="docId ? undefined : (open ? 'true' : 'false')"
      :aria-controls="docId ? undefined : id"
      :title="title"
      @click="onTrigger"
      @keydown.enter.prevent="onTrigger"
      @keydown.space.prevent="onTrigger"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        aria-hidden="true"
        viewBox="0 0 24 24"
      >
        <circle
          cx="12"
          cy="12"
          r="10"
          fill="currentColor"
          opacity=".08"
        />
        <path
          d="M12 17v-5m0-3h.01"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
        />
      </svg>
      <span class="sr-only">Hilfe</span>
    </button>

    <!-- Popover nur wenn KEIN docId vorhanden ist -->
    <transition
      v-if="!docId && !appendToBody"
      name="fade-scale"
    >
      <div
        v-if="open"
        :id="id"
        ref="panel"
        role="dialog"
        class="help-popover"
        :style="computedStyle"
      >
        <header class="help-popover__header">
          <strong>{{ title }}</strong>
          <button
            class="close-btn"
            type="button"
            aria-label="Schließen"
            @click="open=false"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                d="M6 6l12 12M18 6l-12 12"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </header>
        <div class="help-popover__content">
          <slot />
        </div>
      </div>
    </transition>

    <Teleport
      v-else-if="!docId"
      to="body"
    >
      <transition name="fade-scale">
        <div
          v-if="open"
          :id="id"
          ref="panel"
          role="dialog"
          class="help-popover help-popover--fixed"
          :style="fixedStyle"
        >
          <header class="help-popover__header">
            <strong>{{ title }}</strong>
            <button
              class="close-btn"
              type="button"
              aria-label="Schließen"
              @click="open=false"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  d="M6 6l12 12M18 6l-12 12"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </button>
          </header>
          <div class="help-popover__content">
            <slot />
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch, nextTick } from 'vue'
import {useRouter} from 'vue-router'

const props = defineProps({
  title: { type: String, required: true },
  width: { type: [Number, String], default: 500 },
  id: { type: String, default: () => `help-${Math.random().toString(36).slice(2)}` },
  appendToBody: { type: Boolean, default: false },

  docId: { type: String, default: '' }
})

const router = useRouter()

const open = ref(false)
const root = ref(null)
const panel = ref(null)

/* Breite (beide Varianten teilen sich diese Logik) */
const computedStyle = computed(() => ({
  width: typeof props.width === 'number' ? `${props.width}px` : props.width
}))

/* Fixed-Position (Teleport-Variante) */
const fixedTop = ref(0)
const fixedLeft = ref(0)
const fixedStyle = computed(() => {
  const baseWidth = computedStyle.value.width
  return { width: baseWidth, top: `${fixedTop.value}px`, left: `${fixedLeft.value}px` }
})

function parsePxWidth(val) {
  if (typeof val === 'number') return val
  if (typeof val === 'string') {
    const m = /(\d+(?:\.\d+)?)px/.exec(val)
    if (m) return Number(m[1])
    const n = Number(val)
    return Number.isFinite(n) ? n : 0
  }
  return 0
}

function updatePosition() {
  if (!props.appendToBody || !root.value) return
  const trigger = root.value.getBoundingClientRect()
  let panelW = 0
  if (panel.value && panel.value.offsetWidth) {
    panelW = panel.value.offsetWidth
  } else {
    panelW = parsePxWidth(computedStyle.value.width) || 360
  }
  const gap = 8
  const vw = window.innerWidth
  const vh = window.innerHeight

  const top = Math.min(trigger.bottom + gap, vh - gap)
  let left = trigger.right - panelW
  if (left < gap) left = gap
  if (left + panelW > vw - gap) left = Math.max(gap, vw - panelW - gap)

  fixedTop.value = top
  fixedLeft.value = left
}

function openDocumentationInNewTab() {
  if (!props.docId) return;

  const target = router.resolve({name: 'Documentation', query: {doc: props.docId}});
  const url = target?.href || `/documentation?doc=${encodeURIComponent(props.docId)}`;

  window.open(url, '_blank', 'noopener');
}

function onTrigger() {
  if (props.docId) {
    openDocumentationInNewTab();
    return;
  }
  open.value = !open.value;
}

function onDocumentPointerDown (e) {
  if (!root.value) return
  const target = e.target

  if (props.docId) {
    return;
  }

  if (open.value && props.appendToBody) {
    if (panel.value && (panel.value === target || panel.value.contains(target))) {
      return
    }
  }
  if (!root.value.contains(target)) open.value = false
}

function onWindowChanged() {
  if (props.docId) return;
  if (open.value && props.appendToBody) updatePosition()
}

watch(open, async (v) => {
  if (props.docId) return;

  if (v && props.appendToBody) {
    await nextTick()
    updatePosition()
    window.addEventListener('resize', onWindowChanged, { passive: true })
    window.addEventListener('scroll', onWindowChanged, { passive: true })
  } else {
    window.removeEventListener('resize', onWindowChanged)
    window.removeEventListener('scroll', onWindowChanged)
  }
})

onMounted(() => document.addEventListener('pointerdown', onDocumentPointerDown))
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocumentPointerDown)
  window.removeEventListener('resize', onWindowChanged)
  window.removeEventListener('scroll', onWindowChanged)
})
</script>

<style scoped>
.ui-info { position: relative; display: inline-block; }

/* kompakter Icon-Button, unabhängig von globalen Styles */
.help-trigger {
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; min-width: 30px;
  border-radius: 6px; border: 1px solid var(--border, #E0E3EA);
  background: var(--surface, #FFF); color: var(--text, #1F2937);
  cursor: pointer;
}
.help-trigger:hover { background: var(--surface-hover, #F2F3F6); }
.help-trigger:focus-visible { outline: 3px solid var(--focus, rgba(79,70,229,.45)); outline-offset: 2px; }

.sr-only {
  position: absolute !important; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0;
}

.help-popover {
  position: absolute; top: calc(100% + 8px); right: 0;
  background: var(--surface, #FFF);
  border: 1px solid var(--border, #E0E3EA);
  border-radius: 10px; box-shadow: 0 8px 28px rgba(15,23,42,.08), 0 1px 0 rgba(0,0,0,.04);
  padding: 10px 12px; z-index: 40;
}

/* Teleport-/Fixed-Variante: immer über allen Dialog-Inhalten */
.help-popover--fixed {
  position: fixed;
  z-index: 4000; /* über Modals/Sticky/Absolute */
}

.help-popover__header {
  display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 6px;
}

.close-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 26px; height: 26px; border-radius: 6px; border: 1px solid var(--border, #E0E3EA);
  background: var(--surface, #FFF); color: var(--text, #1F2937); cursor: pointer;
}
.close-btn:hover { background: var(--surface-hover, #F2F3F6); }
.close-btn:focus-visible { outline: 3px solid var(--focus, rgba(79,70,229,.45)); outline-offset: 2px; }

.help-popover__doclink {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--border, #E0E3EA);
  display: flex;
  justify-content: flex-end;
}

.doc-btn {
  height: 32px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid var(--border, #E0E3EA);
  background: var(--surface, #FFF);
  color: var(--text, #1F2937);
  cursor: pointer;
}

.doc-btn:hover {
  border-color: var(--border-strong, #CBD5E1);
  background: color-mix(in srgb, var(--primary, #4F46E5) 6%, transparent);
}

.help-popover__content { font-size: 13px; line-height: 1.45; color: var(--text, #1F2937); max-width: 480px; }

/* dezente Motion */
.fade-scale-enter-active, .fade-scale-leave-active { transition: all .12s ease; transform-origin: top right; }
.fade-scale-enter-from, .fade-scale-leave-to { opacity: 0; transform: scale(.98) translateY(-2px); }
</style>
